<template>
  <q-page class="q-px-xl q-pt-xl centered-container">
    <div class="fit row wrap justify-between items-start content-start align-start q-mb-lg">
      <div class="col">
        <q-tabs
          :model-value="section"
          dense
          no-caps
          class="text-grey"
          active-color="primary"
          indicator-color="primary"
          align="left"
          :breakpoint="0"
          shrink
          outside-arrows
          mobile-arrows
        >
          <q-tab
            name="details"
            :label="i18n.tab_details"
            @click="section_change('details')"
          />
          <q-tab
            name="members"
            :label="i18n.tab_members"
            @click="section_change('members')"
          />
          <q-tab
            name="workspace_group_attachment"
            :label="i18n.tab_workspace_group_attachment"
            @click="section_change('workspace_group_attachment')"
          />
        </q-tabs>
      </div>
      <div class="col-shrink">
        <template v-if="section == 'members'">
          <q-btn
            color="primary"
            icon="las la-user-plus"
            unelevated
            :label="$q.screen.gt.xs ? i18n.members_invite_user : void 0"
            no-caps
            @click="invitation_add"
          />
        </template>
      </div>
    </div>

    <div class="fit row wrap justify-between items-start content-start align-start">
      <div class="col-12" />
    </div>
    <q-tab-panels
      :model-value="section"
      animated
      unelevated
    >
      <!-- Details -->
      <q-tab-panel
        name="details"
        class="q-pa-none"
      >
        <div class="row">
          <!-- Workspace Name -->
          <q-card class="q-pa-lg col-12 q-mt-md">
            <q-form
              class="fit row wrap"
              @submit.prevent="workspace_settings_name_form_submit"
            >
              <div class="col-12">
                <span class="text-input-label">{{ i18n.details_name_form_name }}</span>
                <q-input
                  v-model="workspace.name"
                  class="full-width q-mt-sm"
                  outlined
                  dense
                  :rules="[(val) => rule_string_len(val, 1, 40)]"
                />
              </div>

              <div class="col-12 text-right">
                <q-btn
                  vertical
                  type="submit"
                  :loading="workspace_settings_name_form_loading"
                  :disabled="workspace_settings_name_form_loading"
                  :label="$q.lang.label.update"
                  outline
                  color="primary"
                  unelevated
                />
              </div>
            </q-form>
          </q-card>

          <!-- Workspace Business Details -->
          <q-card
            v-if="workspace.is_business"
            class="q-pa-lg q-mt-lg col-12"
          >
            <q-form
              class="row q-col-gutter-md"
              @submit.prevent="workspace_settings_business_form_submit"
            >
              <div class="col-12 col-sm-6">
                <span class="text-input-label">{{ i18n.details_business_form_company_name }}</span>
                <q-input
                  v-model="workspace.company_name"
                  class="full-width q-mt-sm"
                  outlined
                  dense
                  :rules="[(val) => rule_string_len(val, 1, 40)]"
                />
              </div>
              <div class="col-12 col-sm-6">
                <span class="text-input-label">{{ i18n.details_business_form_company_siren }}</span>
                <q-input
                  v-model="workspace.company_siren"
                  class="full-width q-mt-sm"
                  outlined
                  dense
                  :rules="[(val) => rule_string_len(val, 0, 9)]"
                />
              </div>


              <div class="col-12">
                <span class="text-input-label">{{ i18n.details_business_form_company_address }}</span>
                <q-input
                  v-model="workspace.company_address"
                  class="full-width q-mt-sm"
                  outlined
                  dense
                  :rules="[(val) => rule_string_len(val, 1, 60)]"
                />
              </div>
              <div class="col-12 col-md-3 col-sm-6">
                <span class="text-input-label">{{ i18n.details_business_form_company_postal_code }}</span>
                <q-input
                  v-model="workspace.company_postal_code"
                  class="full-width q-mt-sm"
                  outlined
                  dense
                  :rules="[(val) => rule_string_len(val, 1, 10)]"
                />
              </div>
              <div class="col-12 col-md-5 col-sm-6">
                <span class="text-input-label">{{ i18n.details_business_form_company_city }}</span>
                <q-input
                  v-model="workspace.company_city"
                  class="full-width q-mt-sm"
                  outlined
                  dense
                  :rules="[(val) => rule_string_len(val, 1, 40)]"
                />
              </div>
              <div class="col-12 col-md-4 col-sm-12">
                <span class="text-input-label">{{ i18n.details_business_form_company_country }}</span>
                <q-input
                  v-model="workspace.company_country"
                  class="full-width q-mt-sm"
                  outlined
                  dense
                  :rules="[(val) => rule_string_len(val, 1, 40)]"
                />
              </div>

              <div class="col-12 text-right">
                <q-btn
                  vertical
                  type="submit"
                  :loading="workspace_settings_business_form_loading"
                  :disabled="workspace_settings_business_form_loading"
                  :label="$q.lang.label.update"
                  outline
                  color="primary"
                  unelevated
                />
              </div>
            </q-form>
          </q-card>

          <!-- Workspace Individual Details -->
          <q-card
            v-if="!workspace.is_business"
            class="q-pa-lg q-mt-lg col-12"
          >
            <q-form
              class="row q-col-gutter-md"
              @submit.prevent="workspace_settings_individual_form_submit"
            >
              <div class="col-12">
                <span class="text-input-label">
                  {{ i18n.details_individual_form_region }}
                  <q-icon
                    name="las la-question-circle"
                    size="sm"
                    class="q-ml-xs cursor-pointer"
                  >
                    <q-tooltip
                      :delay="500"
                      class="q-card shadow text-caption text-default q-pa-md"
                      style="width: 420px"
                    >
                      {{ i18n.details_individual_form_region_help }}
                    </q-tooltip>
                  </q-icon>
                </span>
              </div>

              <div class="col-12 q-mb-none">
                <q-checkbox
                  v-model="workspace.non_french_resident"
                  :label="i18n.details_individual_form_non_french_resident"
                />
              </div>

              <div
                v-if="!workspace.non_french_resident"
                class="col-12"
              >
                <q-select
                  v-model="workspace.declaration_location"
                  :options="details_locations_fr"
                  option-label="label"
                  option-value="id"
                  class="full-width q-mt-sm"
                  outlined
                  :label="workspace.declaration_location ? undefined : i18n.individual_form_region_field_region_placeholder"
                  dense
                  :rules="[val => !!val || i18n.required]"
                  popup-content-style="height: 300px !important;"
                >
                  <template v-slot:option="scope">
                    <q-item v-bind="scope.itemProps">
                      <q-item-section side>
                        <span class="text-bold text-accent">{{ scope.opt.number }}</span>
                      </q-item-section>
                      <q-item-section>
                        <q-item-label>{{ scope.opt.label }}</q-item-label>
                      </q-item-section>
                    </q-item>
                  </template>
                </q-select>
              </div>

              <p
                class=""
                v-html="workspace.declaration_location_note"
              />

              <div class="col-12 text-right">
                <q-btn
                  vertical
                  type="submit"
                  :loading="workspace_settings_individual_form_loading"
                  :disabled="workspace_settings_individual_form_loading"
                  :label="$q.lang.label.update"
                  outline
                  color="primary"
                  unelevated
                />
              </div>
            </q-form>
          </q-card>

          <!-- Workspace Destroy -->
          <q-card
            v-if="$page.props.auth.user.id == workspace_admin"
            class="q-pa-lg q-mt-lg col-12"
          >
            <div class="row">
              <div class="col-12 text-left">
                <span class="text-input-label">{{ i18n.details_destroy_form_title }}</span>
                <p class="q-mt-md">
                  {{ i18n.details_destroy_form_warning }}
                </p>
              </div>

              <div class="col-12 text-right">
                <q-btn
                  class="q-mt-md"
                  :class="{ 'full-width': $q.screen.lt.sm }"
                  :loading="workspace_settings_destroy_loading"
                  :disable="workspace_settings_destroy_loading"
                  :label="i18n.details_destroy_form_title"
                  icon="las la-radiation-alt"
                  color="negative"
                  unelevated
                  outline
                  style="text-transform: none !important"
                  @click="workspace_settings_destroy_confirm"
                />
              </div>
            </div>
          </q-card>
        </div>
      </q-tab-panel>

      <!-- Members -->
      <q-tab-panel
        name="members"
        class="q-pa-none"
      >
        <div class="row q-col-gutter-md q-mt-md justify-start">
          <div
            v-if="members.length == 0"
            class="col"
          >
            {{ i18n.members_no_users }}
          </div>
          <div
            v-for="user in members"
            :key="user.id"
            class="col-xs-12 col-sm-6 col-md-4 col-lg-3 self-stretch"
          >
            <q-card class="text-center fit">
              <q-card-section class="q-pt-lg">
                <div style="position: absolute; left: 12px; top: 12px">
                  <!-- Admin -->
                  <q-icon
                    v-if="user.is_admin"
                    name="las la-user-shield"
                    class="q-mr-sm"
                    size="26px"
                    color="amber"
                  >
                    <q-tooltip color="primary">
                      {{ i18n.members_icon_admin }}
                    </q-tooltip>
                  </q-icon>

                  <!-- Subscription buyer / Subscriber -->
                  <!--
                  <q-icon
                    v-if="user.is_subscriber"
                    name="las la-credit-card"
                    class="q-mr-sm"
                    size="26px"
                    color="positive"
                  >
                    <q-tooltip color="primary">
                      {{ i18n.members_icon_subscriber }}
                    </q-tooltip>
                  </q-icon>
                  -->
                </div>

                <div style="position: absolute; right: 12px; top: 12px">
                  <!-- CC support user -->
                  <q-avatar
                    v-if="user.is_cc_admin"
                    size="sm"
                  >
                    <img src="/cc_icons/cc_support_badge.svg">
                    <q-tooltip color="primary">
                      {{ i18n.members_icon_cc_admin }}
                    </q-tooltip>
                  </q-avatar>
                </div>

                <div class="column items-center q-pa-md q-pt-lg">
                  <q-avatar size="56px">
                    <img
                      :src="user.avatar_url"
                      referrerpolicy="no-referrer"
                    >
                  </q-avatar>
                  <div class="text-subtitle1 q-mt-md q-mb-xs">
                    {{ user.name }}
                  </div>
                  <div class="text-caption text-grey-6">
                    {{ user.email }}
                  </div>
                </div>
              </q-card-section>
              <q-card-section class="q-pt-md">
                <!-- Revoke access -->
                <q-btn
                  v-if="
                    workspace_manage_members_allowed && !user.is_current_user && !user.is_admin
                  "
                  unelevated
                  :label="i18n.members_action_revoke_access"
                  no-caps
                  flat
                  class="full-width btn-underline"
                  :loading="users_action_loading_id == user.id"
                  @click="user_remove_confirm(user)"
                />

                <!-- Quit Workspace / Delete Workspace -->
                <q-btn
                  v-else-if="user.is_current_user"
                  unelevated
                  :label="i18n.members_action_leave"
                  no-caps
                  flat
                  class="full-width btn-underline"
                  :loading="users_action_loading_id == user.id"
                  @click="user_leave_confirm(user)"
                />
              </q-card-section>
            </q-card>
          </div>

          <div
            v-for="invitation in invitations"
            :key="invitation.id"
            class="col-xs-12 col-sm-6 col-md-4 col-lg-3"
          >
            <q-card class="text-center">
              <q-card-section class="q-pt-lg">
                <q-icon
                  name="las la-hourglass-half"
                  size="26px"
                  color="grey"
                  style="position: absolute; left: 12px; top: 12px"
                />

                <div class="column items-center q-pa-md q-pt-lg">
                  <q-avatar
                    size="56px"
                    color="pastel-orange"
                  >
                    <q-icon
                      name="las la-user-clock"
                      color="dark"
                    />
                  </q-avatar>
                  <div class="text-subtitle1 q-mt-md q-mb-xs">
                    {{ i18n.members_invitation_sent }}
                  </div>
                  <div class="text-caption text-grey-6">
                    {{ invitation.email }}
                  </div>
                </div>
              </q-card-section>
              <q-card-section class="q-pt-md">
                <!-- quit workspace button -->
                <q-btn
                  v-if="workspace_manage_members_allowed"
                  unelevated
                  :label="i18n.members_invitation_cancelation"
                  no-caps
                  outline
                  class="full-width"
                  :loading="invitations_action_loading_id == invitation.id"
                  @click="invitation_cancelation_confirm(invitation)"
                />
              </q-card-section>
            </q-card>
          </div>
        </div>

        <q-dialog
          v-model="invitation_dialog_show"
          persistent
        >
          <q-card
            class="q-pa-xl"
            style="width: 700px; max-width: 80vw"
          >
            <template v-if="invitation_dialog_step == 'form'">
              <div class="row text-center">
                <div class="col-12 text-center">
                  <q-icon
                    name="las la-user-plus"
                    size="56px"
                    class=""
                    color="primary"
                  />
                </div>
                <div class="col-12 q-pt-lg">
                  <span class="text-h6">
                    {{ i18n.members_invite_title }}
                  </span>
                  <div v-if="workspace_add_user_allowed">
                    <p
                      v-if="workspace_remaining_invitation_count != 'unlimited'"
                      class="q-pt-sm"
                    >
                      {{ workspace_remaining_invitation }}
                    </p>
                  </div>

                  <template v-else>
                    <p class="q-pt-sm">
                      {{ i18n.members_invite_disabled }}
                    </p>

                    <div class="col-12">
                      <q-btn
                        color="positive"
                        class="q-mt-lg"
                        icon="las la-shopping-cart"
                        unelevated
                        :label="i18n.members_subscription_upgrade"
                        no-caps
                        @click="goToSubscriptionPage"
                      />
                    </div>

                    <div class="col-12 q-mt-lg">
                      <q-btn
                        :label="$q.lang.label.cancel"
                        flat
                        size="sm"
                        :disable="invite_user_loading"
                        :loading="invite_user_loading"
                        @click="invitation_dialog_close"
                      />
                    </div>
                  </template>
                </div>
              </div>
              <q-form
                v-if="workspace_add_user_allowed"
                class="row justify-end q-mt-lg"
                @submit.prevent="invitation_form_submit"
              >
                <div class="col-12 q-pt-md q-mb-md">
                  <q-input
                    ref="invitation_form_email"
                    v-model="invitation_form_email"
                    :color="invitation_form_email_validation_is_valid ? 'positive' : ''"
                    :disable="invite_user_loading"
                    :loading="invite_user_loading"
                    :rules="[() => invitation_form_email_validation_error || true]"
                    autofocus
                    class="full-width"
                    lazy-rules
                    outlined
                    placeholder="email@example.com"
                    type="email"
                    @blur="invitation_form_email_validate"
                  >
                    <template v-slot:prepend>
                      <q-icon name="las la-envelope" />
                    </template>
                  </q-input>
                </div>
                <div class="col-12 text-right">
                  <q-btn
                    :label="$q.lang.label.cancel"
                    flat
                    class="q-mr-sm"
                    :disable="invite_user_loading"
                    :loading="invite_user_loading"
                    @click="invitation_dialog_close"
                  />
                  <q-btn
                    :label="i18n.members_invite_send"
                    type="submit"
                    color="primary"
                    unelevated
                    :outline="!invitation_form_email_validation_is_valid"
                    :disable="invite_user_loading"
                    :loading="invite_user_loading"
                  />
                </div>
              </q-form>
            </template>

            <template v-if="invitation_dialog_step == 'success'">
              <div class="row text-center">
                <div class="col-12 text-center">
                  <q-icon
                    name="las la-paper-plane"
                    size="56px"
                    class="rotate-270"
                    color="positive"
                  />
                </div>
                <div class="col-12 q-pt-lg">
                  <span class="text-h6">{{ i18n.members_invite_sent_title }}</span>
                  <p class="q-pt-sm">
                    <span v-html="i18n.members_invite_sent_message" /> <strong>{{ invitation_form_email }}</strong>
                  </p>
                </div>
              </div>
              <div class="row text-center q-mt-lg">
                <div class="col-12 text-center">
                  <q-btn
                    :label="$q.lang.label.close"
                    class="q-mr-sm"
                    color="primary"
                    unelevated
                    :disable="invite_user_loading"
                    :loading="invite_user_loading"
                    @click="invitation_dialog_close"
                  />
                </div>
              </div>
            </template>

            <template v-if="invitation_dialog_step == 'error'">
              <div class="row text-center">
                <div class="col-12 text-center">
                  <q-icon
                    name="las la-info-circle"
                    size="56px"
                    color="negative"
                  />
                </div>
                <div class="col-12 q-pt-lg">
                  <span class="text-h6">{{ i18n.members_invite_error_title }}</span>
                  <p class="q-pt-sm text-negative">
                    {{ invite_user_error }}
                  </p>
                </div>
              </div>
              <div class="row text-center q-mt-lg">
                <div class="col-12 text-center">
                  <q-btn
                    :label="$q.lang.label.close"
                    class="q-mr-sm"
                    color="primary"
                    unelevated
                    :disable="invite_user_loading"
                    :loading="invite_user_loading"
                    @click="invitation_dialog_close"
                  />
                </div>
              </div>
            </template>
            <q-inner-loading :showing="invite_user_loading">
              <q-spinner-tail
                color="primary"
                size="64px"
              />
              <p class="q-mt-lg text-bold text-body2">
                {{ i18n.members_invite_sending }}
              </p>
            </q-inner-loading>
          </q-card>
        </q-dialog>
      </q-tab-panel>

      <!-- Workspace Group Attachment -->
      <q-tab-panel
        name="workspace_group_attachment"
        class="q-pa-none"
      >
        <q-card class="q-pa-lg q-mt-md">
          <div class="text-h6 q-mb-md">
            {{ i18n.tab_workspace_group_attachment }}
          </div>

          <template v-if="workspace_group_invitations.length > 0">
            <div class="text-subtitle1 q-mb-md">
              {{ i18n.wg_attachment_current_request }}
            </div>
            <q-card
              class="q-pa-md"
              bordered
            >
              <div class="row items-center">
                <div class="col">
                  <div class="text-body1 text-weight-medium">
                    {{ workspace_group_invitations[0].workspace_group_name }}
                  </div>
                  <div class="text-caption text-grey q-mt-xs">
                    {{ i18n.wg_attachment_status }}: {{ workspace_group_invitations[0].status }}
                  </div>
                </div>
                <div class="col-auto">
                  <q-btn
                    v-if="workspace_manage_members_allowed"
                    :label="i18n.wg_attachment_cancel"
                    outline
                    color="negative"
                    no-caps
                    @click="cancel_workspace_group_invitation(workspace_group_invitations[0])"
                  />
                </div>
              </div>
            </q-card>
          </template>

          <template v-else-if="workspace_manage_members_allowed">
            <q-form @submit.prevent="workspace_group_attachment_submit">
              <div class="q-mb-md">
                <span class="text-input-label">{{ i18n.wg_attachment_siren_label }}</span>
                <q-input
                  v-model="workspace_group_siren"
                  class="full-width q-mt-sm"
                  outlined
                  dense
                  :placeholder="i18n.wg_attachment_siren_placeholder"
                  :rules="[(val) => rule_string_len(val, 1, 50)]"
                />
              </div>

              <div class="text-right">
                <q-btn
                  type="submit"
                  :loading="workspace_group_attachment_loading"
                  :disabled="workspace_group_attachment_loading"
                  :label="i18n.wg_attachment_submit"
                  color="primary"
                  unelevated
                />
              </div>
            </q-form>
          </template>
        </q-card>
      </q-tab-panel>
    </q-tab-panels>
  </q-page>
</template>
<script>
import Layout from '@/layouts/workspace.vue';

export default {
  components: {
  },
  layout: Layout,
  props: {
    workspace_manage_members_allowed: {
      type: Boolean,
      default: false,
    },
    i18n: {
      type: Object,
      required: true,
    },
    workspace_admin: {
      type: String,
      required: true,
    },
    workspace_add_user_allowed: {
      type: Boolean,
      required: true,
    },
    workspace_remaining_invitation_count: {
      type: String, // a number as a string or the string 'unlimited'
      required: true,
    },
    workspace_remaining_invitation: {
      type: String,
      required: true,
    },
    section: {
      type: String,
      required: true,
    },
    details: {
      type: Object,
      required: true,
    },
    details_locations_fr: {
      type: Array,
      required: true,
    },
    members: {
      type: Array,
      required: true,
    },
    invitations: {
      type: Array,
      required: true,
    },
    actions_update_path: {
      type: String,
      required: true,
    },
    actions_invite_add_path: {
      type: String,
      required: true,
    },
    actions_invite_cancel_path: {
      type: String,
      required: true,
    },
    actions_user_remove_path: {
      type: String,
      required: true,
    },
    actions_wg_attachment_path: {
      type: String,
      required: false,
    },
    actions_wg_attachment_cancel_path: {
      type: String,
      required: false,
    },
    workspace_group_invitations: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  data() {
    return {
      workspace_settings_business_form_loading: false,
      workspace_settings_individual_form_loading: false,
      workspace_settings_name_form_loading: false,
      workspace_settings_destroy_loading: false,
      workspace: this.details,
      invitation_dialog_show: false,
      invitation_dialog_step: 'form', // 'form', 'error', 'success'
      invitation_form_email: '',
      invitation_form_email_validation_timer: null,
      invitation_form_email_validation_error: null,
      invitations_action_loading_id: null,
      invite_user_error: '',
      invite_user_loading: false,
      section_tab: this.section,
      users_action_loading_id: null,
      workspace_group_siren: '',
      workspace_group_attachment_loading: false,
    };
  },
  computed: {
  },
  watch: {
    invitation_form_email: function () {
      this.invitation_form_email_validate();
      this.$refs.invitation_form_email?.resetValidation();

      // Delay validation to avoid triggering it too aggressively while typing.
      clearTimeout(this.invitation_form_email_validation_timer);
      this.invitation_form_email_validation_timer = setTimeout(() => {
        this.$refs.invitation_form_email?.validate();
      }, 1000);
    },
  },
  methods: {
    rule_string_len: function (string, min, max) {
      if (!string) {
        return this.i18n.required;
      } else if (string.length < min) {
        return this.i18n.required_min_chars.replace("%{0}", min);
      } else if (string.length > max) {
        return this.i18n.required_max_chars.replace("%{0}", max);
      } else {
        return true;
      }
    },
    workspace_settings_name_form_submit: function () {
      this.$inertia.put(
        this.actions_update_path,
        { name: this.workspace.name },
        {
          preserveScroll: true,
          onStart: () => {
            this.workspace_settings_name_form_loading = true;
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.log(errors);
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
          onFinish: () => {
            this.workspace_settings_name_form_loading = false;
          },
        }
      );
    },
    workspace_settings_business_form_submit: function () {
      this.$inertia.put(
        this.actions_update_path,
        {
          company_name: this.workspace.company_name,
          company_siren: this.workspace.company_siren,
          company_address: this.workspace.company_address,
          company_postal_code: this.workspace.company_postal_code,
          company_city: this.workspace.company_city,
          company_country: this.workspace.company_country,
        },
        {
          preserveScroll: true,
          onStart: () => {
            this.workspace_settings_individual_form_loading = true;
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.log(errors);
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
          onFinish: () => {
            this.workspace_settings_individual_form_loading = false;
          },
        }
      );
    },
    workspace_settings_individual_form_submit: function () {
      this.$inertia.put(
        this.actions_update_path,
        {
          non_french_resident: this.workspace.non_french_resident,
          declaration_location_id: this.workspace.declaration_location?.id ?? null,
        },
        {
          preserveScroll: true,
          onStart: () => {
            this.workspace_settings_individual_form_loading = true;
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.log(errors);
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
          onFinish: () => {
            this.workspace_settings_individual_form_loading = false;
          },
        }
      );
    },
    workspace_settings_destroy_confirm: function () {
      this.$q.dialog({
        title: this.i18n.details_destroy_workspace_confirm_title,
        message: `${this.workspace.name} \n\n ${this.i18n.details_destroy_workspace_confirm_message}`,
        cardClass: "whitespace-pre-line",
        cancel: true,
        color: "primary",
        persistent: true,
      }).onOk(() => {
        this.workspace_settings_destroy();
      });
    },
    workspace_settings_destroy: function () {
      this.$inertia.delete(
        this.actions_update_path,
        {
          preserveScroll: true,
          onStart: () => {
            this.workspace_settings_destroy_loading = true;
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.log(errors);
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
          onFinish: () => {
            this.workspace_settings_destroy_loading = false;
          },
        }
      );
    },
    invitation_dialog_close: function () {
      this.invitation_dialog_show = false;
      this.invitation_dialog_step = 'form';
      this.invitation_form_email = '';
    },
    invitation_form_email_validate: function () {
      const value = this.invitation_form_email;
      const regex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      if (value?.trim() == '') {
        this.invitation_form_email_validation_error = this.i18n.members_invite_email_required;
        this.invitation_form_email_validation_is_valid = null;
      } else if (!regex.test(value)) {
        this.invitation_form_email_validation_error = this.i18n.members_invite_email_invalid;
        this.invitation_form_email_validation_is_valid = null;
      } else if (value?.trim()?.length > 0) {
        this.invitation_form_email_validation_error = null;
        this.invitation_form_email_validation_is_valid = true;
      } else {
        this.invitation_form_email_validation_error = null;
        this.invitation_form_email_validation_is_valid = null;
      }
    },
    invitation_form_submit: function () {
      this.$inertia.post(
        this.actions_invite_add_path,
        { email: this.invitation_form_email },
        {
          preserveScroll: true,
          onStart: () => {
            this.invite_user_loading = true;
          },
          onSuccess: () => {
            this.invitation_dialog_step = 'success';
          },
          onError: (errors) => {
            console.log(errors);
            this.invite_user_error = errors.join(" \n");
            this.invitation_dialog_step = 'error';
          },
          onFinish: () => {
            this.invite_user_loading = false;
          },
        }
      );
    },
    section_change: function (v) {
      this.$inertia.get("", { section: v });
    },
    invitation_add: function () {
      this.invitation_dialog_show = true;
    },
    invitation_cancelation_confirm: function (invitation) {
      this.$q.dialog({
        title: this.i18n.members_invitation_cancelation_confirm_title,
        message: `${invitation.email} \n\n ${this.i18n.members_invitation_cancelation_confirm_message}`,
        cardClass: "whitespace-pre-line",
        cancel: true,
        persistent: true,
        color: "primary",
      }).onOk(() => {
        this.invitation_cancel(invitation);
      });
    },
    invitation_cancel: function (invitation) {
      this.$inertia.post(
        this.actions_invite_cancel_path,
        { invitation_id: invitation.id },
        {
          preserveScroll: true,
          onStart: () => {
            this.invitations_action_loading_id = invitation.id;
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.log(errors);
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
          onFinish: () => {
            this.invitations_action_loading_id = null;
          },
        },
      );
    },
    user_remove_confirm: function (user) {
      this.$q.dialog({
        title: this.i18n.members_user_remove_confirm_title,
        message: `${user.name} (${user.email})\n\n ${this.i18n.members_user_remove_confirm_message}`,
        cardClass: "whitespace-pre-line",
        cancel: true,
        persistent: true,
        color: "primary",
      }).onOk(() => {
        this.user_remove(user);
      });
    },
    user_leave_confirm: function (user) {
      this.$q.dialog({
        title: this.i18n.members_user_leave_confirm_title,
        message: this.i18n.members_user_leave_confirm_message,
        cardClass: "whitespace-pre-line",
        cancel: true,
        persistent: true,
        color: "primary",
      }).onOk(() => {
        this.user_remove(user);
      });
    },
    user_remove: function (user) {
      this.$inertia.post(
        this.actions_user_remove_path,
        { user_id: user.id },
        {
          preserveScroll: true,
          onStart: () => {
            this.users_action_loading_id = user.id;
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.log(errors);
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
          onFinish: () => {
            this.users_action_loading_id = null;
          },
        },
      );
    },
    workspace_group_attachment_submit: function () {
      this.$inertia.post(
        this.actions_wg_attachment_path,
        { siren: this.workspace_group_siren },
        {
          preserveScroll: true,
          onStart: () => {
            this.workspace_group_attachment_loading = true;
          },
          onSuccess: () => {
            this.workspace_group_siren = '';
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.log(errors);
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
          onFinish: () => {
            this.workspace_group_attachment_loading = false;
          },
        }
      );
    },
    cancel_workspace_group_invitation: function (invitation) {
      this.$inertia.post(
        this.actions_wg_attachment_cancel_path,
        { invitation_id: invitation.id },
        {
          preserveScroll: true,
          onSuccess: () => {
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.log(errors);
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
        }
      );
    },
  },
};
</script>
